import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import {
    ListBoxItemClickEvent,
    ListBoxToolbar,
    ListBoxToolbarClickEvent,
    processListBoxData,
} from '@progress/kendo-react-listbox';
import cn from 'clsx';
import { SearchBox } from '../../search-box';
import styles from '../style.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import DragDropCardTitle from '@components-clean/core/drag-drop-lists-core/drag-drop-card-title';
import {
    DefaultDragDropItem,
    DRAG_DROP_FILTER_HEIGHT,
    DRAG_DROP_SELECTED_FIELD,
    filterDragDropColumn,
    sortDragDropItems,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.helper';
import {
    DragDropListItemProps,
    DragDropListsProps,
    ProcessDragDropListResult,
    Side,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import ListBox from '@components-clean/core/drag-drop-lists-core/listbox';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useTranslation } from '@hooks/index';

const DragDropLists = ({
    columnsState,
    setColumnsState,
    onChangeItemsLeft,
    onChangeItemsRight,
    handleDragStart,
    handleDrop,
    handleItemClick,
    list1Header,
    list2Header,
    filterLeft,
    filterRight,
    idField,
    renderItemLeft,
    renderItemRight,
    className,
    disabled,
    filterItem,
    list1HeaderActions,
    hideTransferAllButtons,
    disableReorder,
    sortLefCol,
    handleReset,
}: DragDropListsProps) => {
    const toolbarRef = useRef<HTMLDivElement>(null);
    const filterRefLeft = useRef<HTMLDivElement>(null);
    const filterRefRight = useRef<HTMLDivElement>(null);
    const { readOnly } = useReadOnlyContext();
    const [columnSide, setColumnSide] = useState<Side | ''>('');
    const { t } = useTranslation();

    const handleToolBarClick = (e: ListBoxToolbarClickEvent) => {
        let toolName = e.toolName || '';
        const doSort = toolName === 'transferTo' || toolName === 'transferAllTo';

        // For transfer all, Kendo's processListBoxData method ignores the filter/selected fields.
        // Instead, if using transfer all we will just use the non-all version using the  filtered field instead
        // of the selected field.
        const isTransferAll = toolName === 'transferAllTo' || toolName === 'transferAllFrom';
        if (isTransferAll) {
            toolName = toolName === 'transferAllFrom' ? 'transferFrom' : 'transferTo';
        }

        const result: ProcessDragDropListResult = processListBoxData(
            columnsState.leftColumn,
            columnsState.rightColumn,
            toolName,
            isTransferAll ? 'filtered' : DRAG_DROP_SELECTED_FIELD,
        );

        if (onChangeItemsLeft) onChangeItemsLeft(result.listBoxOneData);
        if (onChangeItemsRight) onChangeItemsRight(result.listBoxTwoData);

        setColumnsState({
            ...columnsState,
            leftColumn: filterDragDropColumn(
                doSort && sortLefCol ? sortDragDropItems(result.listBoxOneData) : result.listBoxOneData,
                filterLeft?.value ?? '',
                idField,
                filterItem,
            ),
            rightColumn: filterDragDropColumn(
                doSort ? sortDragDropItems(result.listBoxTwoData) : result.listBoxTwoData,
                filterRight?.value ?? '',
                idField,
                filterItem,
            ),
        });
    };

    const handleListBoxItemClick = (event: ListBoxItemClickEvent, data: Side, connectedData: Side) => {
        setColumnSide(data);
        handleItemClick(event, data, connectedData);
    };

    const allowMoveUpAndDown =
        ((!filterLeft || filterLeft?.value === '') && columnSide === 'leftColumn') ||
        ((!filterRight || filterRight?.value === '') && columnSide === 'rightColumn');

    return (
        <div data-testid="drag-drop-lists" className={cn(className, styles.alignedListboxes)}>
            <Card
                title={
                    <DragDropCardTitle
                        title={list1Header}
                        numberOfRecords={columnsState.leftColumn.filter((item) => item.filtered).length}
                    />
                }
                headerActions={list1HeaderActions}
                className="k-h-full"
                padding="none"
                data-testid="left-listbox"
            >
                <>
                    {filterLeft && (
                        <div ref={filterRefLeft} style={{ position: 'relative' }}>
                            <SearchBox
                                fillMode="flat"
                                data-testid="search-item-input-left"
                                showSearchIcon
                                value={filterLeft.value}
                                onChange={(e: TextBoxChangeEvent) => filterLeft.setValue((e.value as string) ?? '')}
                                disabled={disabled}
                            />
                        </div>
                    )}
                    <div
                        data-testid="drag-drop-left-list"
                        className="k-h-full"
                        style={{
                            height: `calc(100% - ${
                                filterLeft ? (filterRefLeft.current?.clientHeight ?? DRAG_DROP_FILTER_HEIGHT) : 0
                            }px)`,
                        }}
                    >
                        <ListBox
                            className="w-full !k-h-full"
                            textField="text"
                            data={columnsState.leftColumn.filter((item) => item.filtered)}
                            selectedField={DRAG_DROP_SELECTED_FIELD}
                            onItemClick={(e: ListBoxItemClickEvent) => {
                                handleListBoxItemClick(e, 'leftColumn', 'rightColumn');
                            }}
                            onDragStart={handleDragStart}
                            onDrop={handleDrop}
                            item={renderItemLeft ?? DefaultDragDropItem}
                            draggable={!disabled && !readOnly}
                        />
                    </div>
                </>
            </Card>
            <ListBox
                data={[]}
                textField="text"
                toolbarPosition="left"
                className="fake-listbox k-my-auto"
                style={{ width: toolbarRef?.current?.clientWidth, height: '220px' }}
                hideNoRecordsMessage
                toolbar={() => (
                    <div
                        className={cn('drag-drop-toolbar k-my-auto', { 'disabled-item': disabled || readOnly })}
                        data-testid="drag-drop-toolbar"
                        ref={toolbarRef}
                    >
                        <ListBoxToolbar
                            tools={
                                [
                                    !disableReorder && allowMoveUpAndDown && 'moveUp',
                                    !disableReorder && allowMoveUpAndDown && 'moveDown',
                                    'transferTo',
                                    'transferFrom',
                                    !hideTransferAllButtons && 'transferAllTo',
                                    !hideTransferAllButtons && 'transferAllFrom',
                                ].filter(Boolean) as string[]
                            }
                            data={columnsState.leftColumn}
                            dataConnected={columnsState.rightColumn}
                            onToolClick={handleToolBarClick}
                        />
                        {handleReset && (
                            <div className="k-listbox-actions k-mt-2">
                                <Button
                                    className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-icon-button"
                                    data-testid="multi-list-reset"
                                    onClick={handleReset}
                                    title={t('common:reset')}
                                >
                                    <FontAwesomeIcon icon={['fas', 'undo']} className="fas-undo" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            />
            <Card
                title={
                    <DragDropCardTitle
                        title={list2Header}
                        numberOfRecords={columnsState.rightColumn.filter((item) => item.filtered).length}
                    />
                }
                className="k-h-full"
                padding="none"
                data-testid="right-listbox"
            >
                <>
                    {filterRight && (
                        <div ref={filterRefRight} style={{ position: 'relative' }}>
                            <SearchBox
                                fillMode="flat"
                                data-testid="search-item-input-right"
                                showSearchIcon
                                value={filterRight.value}
                                onChange={(e: TextBoxChangeEvent) => {
                                    filterRight.setValue((e.value as string) ?? '');
                                }}
                                disabled={disabled}
                            />
                        </div>
                    )}
                    <div
                        data-testid="drag-drop-right-list"
                        style={{
                            height: `calc(100% - ${
                                filterRight ? (filterRefRight.current?.clientHeight ?? DRAG_DROP_FILTER_HEIGHT) : 0
                            }px)`,
                        }}
                    >
                        <ListBox
                            className="w-full !k-h-full"
                            textField="text"
                            data={columnsState.rightColumn.filter((item: DragDropListItemProps) => {
                                return item.filtered;
                            })}
                            selectedField={DRAG_DROP_SELECTED_FIELD}
                            onItemClick={(e: ListBoxItemClickEvent) => {
                                const dataItem = e.dataItem as DragDropListItemProps;
                                if (dataItem.disabled) {
                                    const event = e.nativeEvent as Event;
                                    event.preventDefault();
                                    event.stopPropagation();
                                    return;
                                }

                                handleListBoxItemClick(e, 'rightColumn', 'leftColumn');
                            }}
                            onDragStart={handleDragStart}
                            onDrop={handleDrop}
                            item={renderItemRight ?? DefaultDragDropItem}
                            draggable={!disabled && !readOnly}
                        />
                    </div>
                </>
            </Card>
        </div>
    );
};

export default DragDropLists;
